// Building blocks for the staff, ticket, appeal, and community pages. They keep the
// small API those pages were written against, but render the dashboard's own
// components (panels, metrics, empty states, notices) so every page looks the same.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { api, type ApiError } from "../api";
import { Icon, type IconName } from "../icons";
import { Avatar as BaseAvatar, EmptyState as BaseEmptyState, Metric, Panel, Skeleton, humanize } from "../ui";

export type Resource<T> = { loading: boolean; data?: T; error?: ApiError; reload: () => void };

export function useResource<T>(url: string | null): Resource<T> {
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<{ url: string | null; loading: boolean; data?: T; error?: ApiError }>({ url, loading: Boolean(url) });
  useEffect(() => {
    if (!url) return;
    let active = true;
    // Reloading the same URL keeps the current data on screen, and so does a failed
    // reload; a new URL starts empty so nothing from the previous one leaks through.
    setState((current) => ({ url, loading: true, data: current.url === url ? current.data : undefined }));
    api<T>(url).then((data) => active && setState({ url, loading: false, data }))
      .catch((error) => active && setState((current) => ({ url, loading: false, error, data: current.data })));
    return () => { active = false; };
  }, [url, tick]);
  const reload = useCallback(() => setTick((value) => value + 1), []);
  const { loading, data, error } = state.url === url ? state : { loading: Boolean(url), data: undefined, error: undefined };
  return { loading, data, error, reload };
}

// Follows value after it has stopped changing for the given delay, e.g. for search boxes.
export function useDebounced<T>(value: T, delay = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

export const number = (value: unknown) => value === null || value === undefined || !Number.isFinite(Number(value)) ? "—" : new Intl.NumberFormat().format(Number(value));
export const compact = (value: unknown) => value === null || value === undefined || !Number.isFinite(Number(value)) ? "—" : new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(Number(value));
export const hours = (seconds: unknown) => { const value = Number(seconds); if (!Number.isFinite(value)) return "—"; const h = Math.floor(value / 3600), m = Math.floor((value % 3600) / 60); return h ? `${number(h)}h ${m}m` : `${m}m`; };
export const longDate = (value?: string | null) => { if (!value) return "—"; const parsed = Date.parse(value); return Number.isNaN(parsed) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(parsed); };
export const dateTime = (value?: string | null) => { if (!value) return "—"; const parsed = Date.parse(value); return Number.isNaN(parsed) ? value : new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(parsed); };
export const relative = (value?: string | null) => {
  if (!value) return "";
  const diff = (Date.parse(value) - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [["year", 31536000], ["month", 2592000], ["day", 86400], ["hour", 3600], ["minute", 60]];
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, size] of units) if (Math.abs(diff) >= size) return format.format(Math.round(diff / size), unit);
  return "just now";
};

export function Avatar({ src, name, size = 40, square = false }: { src?: string | null; name: string; size?: number; square?: boolean }) {
  return <BaseAvatar src={src} name={name} size={size} square={square}/>;
}

export function PlayerHead({ name, size = 40 }: { name?: string | null; size?: number }) {
  return <BaseAvatar square size={size} name={name || "?"} src={name ? `https://mc-heads.net/avatar/${encodeURIComponent(name)}/${size * 2}` : null}/>;
}

export function CopyButton({ value, label = "Copy", ariaLabel, className = "button" }: { value: string; label?: string; ariaLabel?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (!copied) return; const timer = setTimeout(() => setCopied(false), 2200); return () => clearTimeout(timer); }, [copied]);
  return <button type="button" className={className} aria-label={copied ? undefined : ariaLabel} onClick={() => navigator.clipboard?.writeText(value).then(() => setCopied(true)).catch(() => setCopied(false))}>
    <Icon name={copied ? "check" : "copy"} size={16}/><span aria-live="polite">{copied ? "Copied" : label}</span>
  </button>;
}

const DOT_TONE = { live: "success", idle: "warning", down: "danger" } as const;
export function LiveDot({ state, label }: { state: "live" | "idle" | "down"; label?: string }) {
  return label
    ? <span className={`status-dot tone-${DOT_TONE[state]}`} role="img" aria-label={label}/>
    : <span className={`status-dot tone-${DOT_TONE[state]}`} aria-hidden="true"/>;
}

// A titled panel. The icon is accepted for compatibility; panels in this design are titled by text alone.
export function Card({ title, action, children, className = "" }: { title?: ReactNode; icon?: IconName; action?: ReactNode; children: ReactNode; className?: string }) {
  return <Panel title={title} action={action} className={className}>{children}</Panel>;
}

export function Stat({ label, value, hint, icon }: { label: string; value: ReactNode; hint?: ReactNode; icon?: IconName }) {
  return <Metric label={label} value={value} hint={hint} icon={icon}/>;
}

const NOTICE_TONE = { info: "info", good: "success", bad: "danger" } as const;
const NOTICE_ICON = { info: "info", good: "check", bad: "alert" } as const;
export function Notice({ tone = "info", children }: { tone?: "info" | "good" | "bad"; children: ReactNode }) {
  return <div className={`notice tone-${NOTICE_TONE[tone]}`} role={tone === "bad" ? "alert" : "status"}><Icon name={NOTICE_ICON[tone]} size={18}/><p>{children}</p></div>;
}

export function EmptyState({ icon = "spark", title, children, action }: { icon?: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return <BaseEmptyState icon={icon} title={title} description={children} action={action} compact/>;
}

export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return <Skeleton rows={rows}/>;
}

export function ErrorState({ error, fallback = "This information is not available right now." }: { error?: ApiError; fallback?: string }) {
  const code = error?.error?.code;
  const locked = code === "page_restricted" || code === "forbidden" || code === "sign_in_required";
  const title = code === "page_restricted" || code === "forbidden" ? "Not available for your roles" : code === "sign_in_required" ? "Sign in to see this" : code === "feature_unavailable" ? "Not connected yet" : code ? humanize(code) : "Could not load this";
  return <div className={`empty compact ${locked ? "" : "tone-error"}`} role={locked ? undefined : "alert"}>
    <span className="empty-icon"><Icon name={locked ? "lock" : "alert"} size={22}/></span>
    <h3>{title}</h3><p>{error?.error?.message || fallback}</p>
    {error?.error?.requestId && <small className="fine-print">Request {error.error.requestId}</small>}
  </div>;
}

// Thirty-day bar chart used on the portal, staff directory, and ticket insights.
export function Bars({ series, valueKey, label, format }: { series: Array<Record<string, any>>; valueKey: string; label: string; format: (value: number) => string }) {
  const max = Math.max(1, ...series.map((row) => Number(row[valueKey]) || 0));
  const total = series.reduce((sum, row) => sum + (Number(row[valueKey]) || 0), 0);
  return <figure className="bar-chart" aria-label={`${label}, last ${series.length} days, total ${format(total)}`}>
    <div className="bar-chart-plot">{series.map((row) => { const value = Number(row[valueKey]) || 0; return <span key={row.day} style={{ height: `${Math.max(value ? 6 : 2, (value / max) * 100)}%` }} className={value ? "" : "zero"} title={`${longDate(row.day)}: ${format(value)}`}/>; })}</div>
    <figcaption><span>{longDate(series[0]?.day)}</span><span>Today</span></figcaption>
  </figure>;
}
